/**
 * The staff console's client for `/api/support/desk`.
 *
 * The other side of the conversation `lib/support.ts` opens. A learner asks a
 * question there; somebody answers it here, at `/support`, which is a page of
 * this same bundle with no virtual machine behind it (main.tsx).
 *
 * ## Why this shares no code with lib/support.ts
 *
 * Deliberately, and it is the reason the two files repeat a thread's shape
 * between them. A learner's tab downloads `lib/support.ts` on every visit and
 * has no business carrying the queue, the roster, the customer record or the
 * canned replies — none of which it may read, all of which would sit in the
 * chunk anyway. The console is loaded lazily from one branch of main.tsx and
 * pays for itself only when somebody opens it. Two small readers that agree
 * with the same Worker are cheaper than one shared module that puts the
 * desk's vocabulary in front of every learner.
 *
 * The shapes below are `worker/support.ts` §2.3 verbatim. Where the two files
 * describe the same row — a thread — the names are the spec's, so a reader
 * comparing the Worker against either finds the same words.
 *
 * ## The rule that is different here
 *
 * `lib/support.ts` collapses every failure to an empty snapshot, because
 * nothing about support may break the chat. This module must *not* do that,
 * and the difference matters: a console that drew an empty queue when the
 * network dropped would tell an agent on shift that nobody is waiting. So a
 * read has three answers and they are kept apart — the desk as it is, "you
 * may not have this" (404, which is also what signed-out and not-on-the-roster
 * look like, on purpose), and "could not ask". The page says something
 * different for each.
 *
 * ## Why 404 is the only refusal
 *
 * Every route in the module answers 404 rather than 401 or 403 when the
 * caller may not have it — signed out, not staff, not theirs, feature off.
 * Probing therefore learns nothing: `/support` looks the same to a stranger,
 * to a learner and to an agent whose grant was removed this morning. Nothing
 * in this file may leak the distinction back out, which is why there is one
 * `denied` reason and not four.
 */
import { parseRoomId, type RoomId, type SupportTopic } from '@engineer-os/chat';
import { chatUrl } from './chat';

/* ── The address ────────────────────────────────────────────────────────── */

/**
 * Is this the console's address?
 *
 * Read in main.tsx before React mounts, for the reason `isProjectsPath` is:
 * the branch decides whether `App` runs, and `App` boots a hundred megabytes
 * of Linux. An agent who opened `/support` to answer a question has no use
 * for a virtual machine, and getting this regular expression wrong costs the
 * download rather than the page.
 *
 * Anything under `/support` counts, not only the exact path. The asset store
 * answers every path below it with the same `index.html`
 * (`not_found_handling = "single-page-application"`), so `/support/` and a
 * stale `/support/threads/x` from somebody's bookmark are the console rather
 * than a boot screen.
 */
export function isSupportPath(pathname: string): boolean {
  return /^\/support(\/.*)?$/.test(pathname);
}

/* ── The shapes, as worker/support.ts §2.3 states them ──────────────────── */

export type SupportState = 'open' | 'resolved';
export type SupportFrom = 'learner' | 'staff';
/** The two languages a thread can have been opened in. */
export type SupportLocale = 'en' | 'ar';

/** One conversation, as both ends are told about it. */
export interface SupportThreadView {
  id: string;
  /**
   * The chat room it happens in, already branded.
   *
   * Carried rather than derived from the id, for the reason `Team.room` is:
   * the room id belongs to the protocol (`supportRoom`, packages/chat), and a
   * console that assembled one by concatenation would open a socket to a room
   * nobody is in the day the family gains a prefix — silently, because an
   * empty room and an empty conversation look alike.
   */
  room: RoomId;
  topic: SupportTopic;
  subject: string | null;
  state: SupportState;
  opened: number;
  lastTs: number;
  lastFrom: SupportFrom;
  /** The display name of whoever holds it, or null while nobody does. */
  agent: string | null;
}

/** Somebody on the roster, and whether they are here now. */
export interface DeskAgent {
  userId: string;
  display: string;
  /** Their last desk read was recent enough to count. Reading the queue is clocking on. */
  onShift: boolean;
  seenAt: number;
  /** Open threads assigned to them. */
  holding: number;
}

/** A row of the queue. Enough to choose from, and no conversation in it. */
export interface DeskQueueRow {
  id: string;
  room: RoomId;
  topic: SupportTopic;
  subject: string | null;
  state: SupportState;
  opened: number;
  lastTs: number;
  lastFrom: SupportFrom;
  /** `state === 'open' && lastFrom === 'learner'`: the ball is with us. */
  waiting: boolean;
  /** The holder's user id, or null. */
  assignee: string | null;
  assigneeDisplay: string | null;
  /** The account name of whoever asked. */
  learner: string;
  locale: SupportLocale;
}

/**
 * Who the agent is talking to.
 *
 * Every field here is something an agent would otherwise ask the learner for
 * and then have to believe — "have you paid", "which level are you on", "when
 * did you sign up". Asking is slow, and a support conversation that begins
 * with four questions the deployment already knows the answers to is a
 * conversation that starts by wasting the customer's time.
 *
 * `tag` is the chat tag, and it is here for one narrow purpose: a conversation
 * that turns nasty can be carried to `scripts/chat-moderate.mjs ban <tag>` at
 * a terminal. The console itself cannot hide, ban or purge anything, and the
 * presence of the tag is not a step towards it being able to.
 */
export interface DeskCustomer {
  name: string;
  tag: string;
  joined: number;
  seen: number;
  locale: SupportLocale;
  paid: boolean;
  entitlement: { source: string; granted: number } | null;
  /** Null when they have never attempted a level, which is not the same as zero. */
  record: { cleared: number; stars: number; xp: number } | null;
  checkpoints: number;
  team: string | null;
}

/** A line only staff see. It is not in the conversation and never reaches the room. */
export interface DeskNote {
  id: number;
  author: string;
  ts: number;
  text: string;
}

/** A saved answer, in both languages, because the learner asked in one of them. */
export interface CannedReply {
  slug: string;
  title: string;
  en: string;
  ar: string;
  updated: number;
}

export interface DeskCounts {
  waiting: number;
  open: number;
  mine: number;
}

/** The queue read: `GET /api/support/desk`, which is also how an agent clocks on. */
export interface DeskSnapshot {
  /**
   * The reader's own roster row.
   *
   * §2.3 names the field and not its shape. It is read as a `DeskAgent`
   * because that is the only shape in the module that describes a member of
   * the roster, and because the console needs exactly what one carries: the
   * user id, to tell "held by me" from "held by somebody", and the display,
   * to say which of the names in the presence strip is the reader's. Null is
   * tolerated rather than assumed impossible — a Worker one deploy behind may
   * not send it, and the console is usable without it.
   */
  me: DeskAgent | null;
  agents: DeskAgent[];
  queue: DeskQueueRow[];
  counts: DeskCounts;
}

/** One conversation opened: `GET /api/support/desk/threads/<id>`. */
export interface DeskThread {
  thread: SupportThreadView;
  customer: DeskCustomer | null;
  notes: DeskNote[];
}

/** Nothing, and a shape the console can draw before the first answer arrives. */
export const NO_DESK: DeskSnapshot = {
  me: null,
  agents: [],
  queue: [],
  counts: { waiting: 0, open: 0, mine: 0 },
};

/**
 * Three answers, not two.
 *
 * `denied` is the 404 every route gives whoever may not have it, and it is the
 * one the quiet sign-in page is drawn for. `offline` is a fetch that did not
 * complete or a Worker that answered with something else, and it must never be
 * drawn as an empty desk: an agent looking at "nothing is waiting" has been
 * told a fact, and a failed request is not one.
 */
export type DeskRead<T> = { ok: true; value: T } | { ok: false; reason: 'denied' | 'offline' };

/**
 * `credentials: 'include'` and `no-store`, the pair every account-shaped read
 * in this bundle uses (`lib/inbox.ts`, `lib/teams.ts`, `lib/support.ts`): a
 * static build can be pointed at a Worker on another origin with
 * `VITE_CHAT_ORIGIN`, and nothing here may ever sit in a cache somebody else
 * can read. That matters more on this module than on any other — a cached
 * `DeskCustomer` is one customer's paid status in another agent's browser.
 */
const SESSION: RequestInit = { credentials: 'include', cache: 'no-store' };

/**
 * Every POST carries a JSON body and says so.
 *
 * There is no CSRF token in this Worker and there must not be one. The stated
 * defence is `SameSite=Lax` on the session cookie plus a mandatory JSON
 * content type, which a cross-site form post cannot set — so the header below
 * is load-bearing rather than decoration, and a request without it is refused
 * on purpose.
 */
const WRITE: RequestInit = {
  ...SESSION,
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
};

/* ── Reading what the Worker said ───────────────────────────────────────── */

/**
 * Every field is re-checked rather than trusted.
 *
 * Not because the Worker is suspect but because an old deploy is: a tab left
 * open across a release may be talking to a version that predates a field, and
 * the failure of trusting the parse is a `NaN` in a timestamp or an
 * `undefined` where a name belongs, surfacing three components from the fetch
 * that caused it. `lib/projects.ts`, `lib/inbox.ts` and `lib/support.ts` all
 * read their answers this way; this is the fourth copy of the same judgement
 * and the first where a wrong read is shown to somebody about somebody else.
 */
const TOPICS: readonly string[] = ['access', 'billing', 'tasks', 'content', 'bug', 'other'];

function obj(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function stamp(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function locale(value: unknown): SupportLocale {
  return value === 'ar' ? 'ar' : 'en';
}

export function readThread(raw: unknown): SupportThreadView | null {
  const d = obj(raw);
  if (!d) return null;
  const id = str(d.id);
  // The room is parsed rather than cast. `parseRoomId` is the protocol's own
  // reader and it knows the `support-<22>` family; anything it refuses is a
  // string this console must not open a socket to, whoever sent it.
  const room = parseRoomId(d.room);
  if (!id || !room) return null;
  if (typeof d.topic !== 'string' || !TOPICS.includes(d.topic)) return null;
  return {
    id,
    room,
    topic: d.topic as SupportTopic,
    subject: str(d.subject),
    state: d.state === 'resolved' ? 'resolved' : 'open',
    opened: stamp(d.opened),
    lastTs: stamp(d.lastTs),
    lastFrom: d.lastFrom === 'staff' ? 'staff' : 'learner',
    agent: str(d.agent),
  };
}

function readAgent(raw: unknown): DeskAgent | null {
  const d = obj(raw);
  if (!d) return null;
  const userId = str(d.userId);
  const display = str(d.display);
  if (!userId || !display) return null;
  return { userId, display, onShift: d.onShift === true, seenAt: stamp(d.seenAt), holding: count(d.holding) };
}

function readQueueRow(raw: unknown): DeskQueueRow | null {
  const thread = readThread(raw);
  const d = obj(raw);
  if (!thread || !d) return null;
  return {
    ...thread,
    // Derived here as well as read, and the local derivation wins when the
    // field is missing. "Waiting" is the one thing the whole queue is sorted
    // and coloured by, and its definition — open, and the last word was the
    // learner's — is already on the row. A Worker that does not send it
    // should not empty the queue's only signal.
    waiting: typeof d.waiting === 'boolean' ? d.waiting : thread.state === 'open' && thread.lastFrom === 'learner',
    assignee: str(d.assignee),
    assigneeDisplay: str(d.assigneeDisplay),
    learner: str(d.learner) ?? '',
    locale: locale(d.locale),
  };
}

function readCustomer(raw: unknown): DeskCustomer | null {
  const d = obj(raw);
  if (!d) return null;
  const name = str(d.name);
  if (!name) return null;
  const ent = obj(d.entitlement);
  const rec = obj(d.record);
  return {
    name,
    tag: str(d.tag) ?? '',
    joined: stamp(d.joined),
    seen: stamp(d.seen),
    locale: locale(d.locale),
    paid: d.paid === true,
    entitlement: ent ? { source: str(ent.source) ?? '', granted: stamp(ent.granted) } : null,
    // Null and zero are different answers and the difference is worth keeping:
    // null is "has never opened a level", zero is "has opened levels and
    // cleared none". An agent reading the second is looking at somebody who is
    // stuck, which is the whole reason they may have written in.
    record: rec ? { cleared: count(rec.cleared), stars: count(rec.stars), xp: count(rec.xp) } : null,
    checkpoints: count(d.checkpoints),
    team: str(d.team),
  };
}

function readNote(raw: unknown): DeskNote | null {
  const d = obj(raw);
  if (!d) return null;
  if (typeof d.id !== 'number' || typeof d.text !== 'string') return null;
  return { id: d.id, author: str(d.author) ?? '', ts: stamp(d.ts), text: d.text };
}

function readReply(raw: unknown): CannedReply | null {
  const d = obj(raw);
  if (!d) return null;
  const slug = str(d.slug);
  const title = str(d.title);
  if (!slug || !title) return null;
  // Both bodies are required. A saved answer with one language missing is a
  // saved answer that inserts an empty message into somebody's conversation
  // the first time it is used on a thread opened in the other one.
  const en = str(d.en);
  const ar = str(d.ar);
  if (!en || !ar) return null;
  return { slug, title, en, ar, updated: stamp(d.updated) };
}

function list<T>(raw: unknown, read: (row: unknown) => T | null): T[] {
  return Array.isArray(raw) ? raw.map(read).filter((row): row is T => row !== null) : [];
}

export function readDesk(raw: unknown): DeskSnapshot {
  const d = obj(raw);
  if (!d) return NO_DESK;
  const counts = obj(d.counts);
  return {
    me: readAgent(d.me),
    agents: list(d.agents, readAgent),
    queue: list(d.queue, readQueueRow),
    counts: {
      waiting: count(counts?.waiting),
      open: count(counts?.open),
      mine: count(counts?.mine),
    },
  };
}

/* ── The calls ──────────────────────────────────────────────────────────── */

/**
 * One request, and the three answers this module is allowed to give.
 *
 * Written once rather than at every call site because the mapping is the
 * whole security posture of the page in three lines, and a route that forgot
 * to treat 404 as `denied` would be a route that showed an agent an error
 * message where the quiet page belongs — which is itself a signal about
 * whether the roster exists.
 */
async function ask<T>(path: string, read: (raw: unknown) => T | null, init?: RequestInit): Promise<DeskRead<T>> {
  try {
    const r = await fetch(chatUrl(path).href, init ?? SESSION);
    if (r.status === 404) return { ok: false, reason: 'denied' };
    if (!r.ok) return { ok: false, reason: 'offline' };
    const value = read(await r.json());
    return value === null ? { ok: false, reason: 'offline' } : { ok: true, value };
  } catch {
    // An abort lands here too, and reads as `offline`. The caller that
    // aborted has already moved on and is not looking at the answer; the one
    // case that matters is a genuine network failure, and it is this.
    return { ok: false, reason: 'offline' };
  }
}

/** The queue, the roster and the counts — and, at the server, a clock-on. */
export function fetchDesk(signal?: AbortSignal): Promise<DeskRead<DeskSnapshot>> {
  return ask('/api/support/desk', (raw) => readDesk(raw), { ...SESSION, signal });
}

/** One conversation's row, its customer and its notes. */
export function fetchThread(id: string, signal?: AbortSignal): Promise<DeskRead<DeskThread>> {
  return ask(
    `/api/support/desk/threads/${encodeURIComponent(id)}`,
    (raw) => {
      const d = obj(raw);
      const thread = readThread(d?.thread);
      if (!thread) return null;
      // The customer may be absent — an account deleted between the queue read
      // and this one — and that is a conversation the agent can still read and
      // close rather than a failed request.
      return { thread, customer: readCustomer(d?.customer), notes: list(d?.notes, readNote) };
    },
    { ...SESSION, signal },
  );
}

/**
 * What a claim can say that the other three actions cannot.
 *
 * `held` is the 409: somebody else has this and is on shift. It carries their
 * name because "taken" without a name is a dead end, and the agent's next move
 * is to ask that person rather than to press the button again. Unassigned,
 * already yours, and held by somebody who has gone home all succeed — a queue
 * that stays locked by whoever clicked it before lunch is a queue that stops
 * being worked.
 */
export type DeskAct =
  | { ok: true; thread: SupportThreadView }
  | { ok: false; reason: 'held'; heldBy: string | null }
  | { ok: false; reason: 'denied' | 'offline' };

async function act(id: string, action: string): Promise<DeskAct> {
  try {
    const r = await fetch(chatUrl(`/api/support/desk/threads/${encodeURIComponent(id)}/${action}`).href, {
      ...WRITE,
      body: '{}',
    });
    if (r.status === 404) return { ok: false, reason: 'denied' };
    if (r.status === 409) {
      const body = obj(await r.json().catch(() => null));
      return { ok: false, reason: 'held', heldBy: str(body?.heldBy) };
    }
    if (!r.ok) return { ok: false, reason: 'offline' };
    const thread = readThread(obj(await r.json())?.thread);
    return thread ? { ok: true, thread } : { ok: false, reason: 'offline' };
  } catch {
    return { ok: false, reason: 'offline' };
  }
}

/** Take a conversation. */
export const claimThread = (id: string): Promise<DeskAct> => act(id, 'claim');
/** Put it back in the queue, which is the honest thing to do before going home. */
export const releaseThread = (id: string): Promise<DeskAct> => act(id, 'release');
/** It is answered. Nothing is deleted; the learner writing again reopens it. */
export const resolveThread = (id: string): Promise<DeskAct> => act(id, 'resolve');
/** It was not answered after all. */
export const reopenThread = (id: string): Promise<DeskAct> => act(id, 'reopen');

/** Leave a line for whoever picks this up next. Never reaches the room. */
export async function addNote(id: string, text: string): Promise<DeskRead<DeskNote>> {
  return ask(`/api/support/desk/threads/${encodeURIComponent(id)}/notes`, (raw) => readNote(obj(raw)?.note), {
    ...WRITE,
    body: JSON.stringify({ text }),
  });
}

/** Every saved answer this deployment keeps. */
export function fetchReplies(signal?: AbortSignal): Promise<DeskRead<CannedReply[]>> {
  return ask('/api/support/desk/replies', (raw) => list(obj(raw)?.replies, readReply), { ...SESSION, signal });
}

/** What saving a canned reply can fail at, past the two every call can fail at. */
export type ReplyWrite = { ok: true; reply: CannedReply } | { ok: false; reason: 'full' | 'bad' | 'denied' | 'offline' };

/**
 * Write a saved answer, by slug, creating or replacing.
 *
 * The slug is optional and the Worker derives one from the title when it is
 * absent, which is what makes "write a new answer" one field fewer. Sending it
 * is how an existing answer is edited rather than duplicated under a second
 * slug that differs from the first by a comma in the title.
 */
export async function saveReply(input: { slug?: string; title: string; en: string; ar: string }): Promise<ReplyWrite> {
  try {
    const r = await fetch(chatUrl('/api/support/desk/replies').href, { ...WRITE, body: JSON.stringify(input) });
    if (r.status === 404) return { ok: false, reason: 'denied' };
    if (r.status === 409) return { ok: false, reason: 'full' };
    if (r.status === 400) return { ok: false, reason: 'bad' };
    if (!r.ok) return { ok: false, reason: 'offline' };
    const reply = readReply(obj(await r.json())?.reply);
    return reply ? { ok: true, reply } : { ok: false, reason: 'offline' };
  } catch {
    return { ok: false, reason: 'offline' };
  }
}

/** Remove one. */
export async function deleteReply(slug: string): Promise<boolean> {
  try {
    const r = await fetch(chatUrl(`/api/support/desk/replies/${encodeURIComponent(slug)}`).href, {
      ...SESSION,
      method: 'DELETE',
    });
    return r.ok;
  } catch {
    return false;
  }
}

/* ── Two things the console decides rather than asks ────────────────────── */

/**
 * The body of a saved answer, in the language the conversation was opened in.
 *
 * The one rule about canned replies that is not a matter of taste: the learner
 * asked in a language and is owed the answer in it, and an agent reading the
 * console in English who inserts an English paragraph into an Arabic thread
 * has made the saved answer worse than typing one. The thread's `locale` is
 * carried on the queue row and on the thread view precisely so this choice can
 * be made without asking anybody.
 */
export function replyBody(reply: CannedReply, threadLocale: SupportLocale): string {
  return threadLocale === 'ar' ? reply.ar : reply.en;
}

/**
 * The queue, in the order it should be worked.
 *
 * Waiting first — those are the people sitting in front of a question nobody
 * has answered — and within each group the oldest first, because the longest
 * wait is the one doing the most damage. Resolved rows sink. The server's
 * index is `(state, last_ts)` and returns much the same order; this is applied
 * anyway because the console filters and re-renders locally and a list that
 * reorders itself only on the next poll would move under the pointer.
 */
export function queueOrder(rows: readonly DeskQueueRow[]): DeskQueueRow[] {
  return [...rows].sort((a, b) => {
    if (a.waiting !== b.waiting) return a.waiting ? -1 : 1;
    if ((a.state === 'open') !== (b.state === 'open')) return a.state === 'open' ? -1 : 1;
    return a.lastTs - b.lastTs;
  });
}
